// src/pages/Careers/components/CareersHero.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  FileText,
  ArrowRight,
  Rocket,
  Lightbulb,
  BarChart3,
  Building2,
  Package,
} from "lucide-react";

const featureCards = [
  {
    icon: Rocket,
    title: "Work on Real Products",
    description: "Be part of products that solve real-world business problems.",
  },
  {
    icon: Lightbulb,
    title: "Learn & Grow",
    description: "Gain hands-on experience with modern technologies.",
  },
  {
    icon: Users,
    title: "Collaborative Culture",
    description: "Work with a passionate team that supports your ideas.",
  },
  {
    icon: BarChart3,
    title: "Make an Impact",
    description: "Turn your skills into meaningful solutions for businesses.",
  },
];

const stats = [
  {
    icon: Users,
    value: "30+",
    label: "Happy Clients",
  },
  {
    icon: Users,
    value: "15+",
    label: "Team Members",
  },
  {
    icon: Building2,
    value: "5+",
    label: "Industries Served",
  },
  {
    icon: FileText,
    value: "50+",
    label: "Completed Projects",
  },
  {
    icon: Package,
    value: "2+",
    label: "Products in Development",
  },
];

const CareersHero = ({ onScrollToJobs, onOpenResumeModal }) => {
  return (
    <section className="bg-gray-50/70 text-black font-sans pt-28 lg:pt-32 pb-8 border-b border-gray-200 min-h-[calc(100vh-80px)] flex flex-col justify-center">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full">
        {/* Top Split Layout: Left Content & Right 2x2 Feature Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch mb-6">
          {/* Left Column */}
          <div className="lg:col-span-7 text-left flex flex-col justify-between py-0.5">
            <div>
              {/* Top Badges */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-wrap items-center gap-3 mb-6"
              >
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium bg-white border border-gray-300 text-gray-800 shadow-xs">
                  <Users className="w-3.5 h-3.5 text-gray-700" />
                  Careers at Crack Developers
                </span>

                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium bg-white border border-gray-300 text-gray-800 shadow-xs">
                  <span className="w-2.5 h-2.5 rounded-full bg-black inline-block" />
                  We're actively building & hiring
                </span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-5xl md:text-6xl lg:text-7xl font-light leading-snug mb-6 text-gray-900"
              >
                Build. Learn. <br />
                <span className="text-gray-400 font-light">Ship. Grow.</span>
              </motion.h1>

              {/* Supporting Text */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-gray-600 max-w-xl mb-8 leading-relaxed font-normal"
              >
                We're building real products, solving real business problems, and growing our team along the way.
              </motion.p>
            </div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center gap-3.5"
            >
              <button
                onClick={onOpenResumeModal}
                className="px-6 py-3.5 rounded-xl font-bold bg-black text-white hover:bg-gray-800 transition duration-300 text-xs md:text-sm shadow-md flex items-center gap-2 group"
              >
                <span>Apply Now</span>
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={onOpenResumeModal}
                className="px-6 py-3.5 rounded-xl font-bold bg-white border border-gray-300 text-black hover:bg-gray-100 hover:border-gray-400 transition duration-300 text-xs md:text-sm flex items-center gap-2 shadow-xs"
              >
                <FileText className="w-4 h-4 text-black" />
                <span>Send Your Resume</span>
              </button>
            </motion.div>
          </div>

          {/* Right Column: 2x2 Feature Cards Grid */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-3.5 h-full">
            {featureCards.map((card, idx) => {
              const IconComp = card.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + idx * 0.1 }}
                  className="p-5 rounded-2xl bg-white shadow-md flex flex-col justify-between"
                >
                  <div>
                    {/* Circle Icon Badge */}
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white mb-3.5 shadow-sm">
                      <IconComp className="w-4 h-4" />
                    </div>

                    {/* Card Title */}
                    <h3 className="text-sm font-bold text-gray-900 mb-1.5">
                      {card.title}
                    </h3>

                    {/* Card Description */}
                    <p className="text-xs text-gray-600 leading-relaxed font-normal">
                      {card.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom Full-Width Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rounded-2xl bg-white p-4 md:p-5 shadow-md"
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-0">
            {stats.map((stat, idx) => {
              const StatIcon = stat.icon;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-gray-50 transition duration-200 ${idx !== stats.length - 1 ? "lg:border-r border-gray-200" : ""
                    }`}
                >
                  <div className="w-9 h-9 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-900 shrink-0">
                    <StatIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-lg md:text-xl font-bold text-gray-900 leading-tight">
                      {stat.value}
                    </div>
                    <div className="text-[11px] text-gray-500 font-medium">
                      {stat.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CareersHero;
